import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

/**
 * Custom hook for scroll-triggered animations
 * Uses Intersection Observer API for performance
 *
 * 既定値は節（高さ 1,000〜2,900px）のラッパーに当てる前提。
 * threshold 0.1 だと節の 1 割が入るまで透明のままで、スクロール中に空白が見えてから
 * 文字が浮く。「下端の 10% 手前まで少しでも入ったら出す」にしている。
 */
export function useScrollAnimation<T extends HTMLElement = HTMLElement>(
  options: UseScrollAnimationOptions = {}
) {
  const {
    threshold = 0,
    rootMargin = '0px 0px -10% 0px',
    triggerOnce = true,
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { elementRef, isVisible };
}

/**
 * Hook for staggered animations with multiple elements
 */
export function useStaggeredAnimation<T extends HTMLElement = HTMLElement>(
  itemCount: number,
  options: UseScrollAnimationOptions = {}
) {
  const { elementRef, isVisible } = useScrollAnimation<T>(options);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(
    new Array(itemCount).fill(false)
  );

  useEffect(() => {
    if (isVisible) {
      // Stagger the animations with 150ms delay between each item
      const timeouts: NodeJS.Timeout[] = [];
      for (let index = 0; index < itemCount; index++) {
        const timeout = setTimeout(() => {
          setVisibleItems((prev) => {
            const newState = [...prev];
            newState[index] = true;
            return newState;
          });
        }, index * 150);
        timeouts.push(timeout);
      }

      return () => {
        timeouts.forEach(clearTimeout);
      };
    }
  }, [isVisible, itemCount]);

  return { elementRef, visibleItems, isVisible };
}
